import { Link, useNavigate } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { parseAsInteger, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { SearchInput } from '@/components/search-input'
import { SearchSelect } from '@/components/search-select'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { cn, resultCount } from '@/lib/utils'
import {
  FILTER_LABELS,
  KIND_LABELS,
  STATUS,
  TAX_EXEMPTION_FILTERS,
  aiCall,
  formatDay,
  statesText,
  useTaxExemptions,
  type TaxExemption,
} from './api'

const FILTER_OPTIONS = TAX_EXEMPTION_FILTERS.filter((value) => value !== 'all').map((value) => ({
  value,
  label: FILTER_LABELS[value],
}))

const AI_TONES = {
  approve: 'text-emerald-700 dark:text-emerald-300',
  decline: 'text-red-700 dark:text-red-300',
  unsure: 'text-amber-800 dark:text-amber-300',
  none: 'text-muted-foreground',
}

/** The customer's name: a link to their customer page when they are one of ours (the row opens the form). */
function Customer({ row }: { row: TaxExemption }) {
  const name = row.customer_name || row.customer_email || 'Store customer'
  return (
    <div className="min-w-0">
      {row.customer_group_uuid ? (
        <Link
          className="block truncate font-medium hover:underline"
          onClick={(event) => event.stopPropagation()}
          params={{ groupId: row.customer_group_uuid }}
          to="/customers/$groupId"
        >
          {name}
        </Link>
      ) : (
        <span className="block truncate font-medium">{name}</span>
      )}
      {row.customer_name && row.customer_email ? (
        <span className="block truncate text-xs text-muted-foreground">{row.customer_email}</span>
      ) : null}
    </div>
  )
}

function OnTheForm({ row }: { row: TaxExemption }) {
  const facts = [
    row.certificate_kind ? KIND_LABELS[row.certificate_kind] : null,
    statesText(row.states),
    row.coverage === 'some' ? 'Some items' : null,
  ].filter(Boolean)
  return (
    <div className="min-w-0">
      <Link
        className={cn('block truncate hover:underline', !row.holder_name && 'text-muted-foreground')}
        onClick={(event) => event.stopPropagation()}
        params={{ formId: row.uuid }}
        to="/tax-exemptions/$formId"
      >
        {row.holder_name || 'No name read'}
      </Link>
      {facts.length ? <span className="block truncate text-xs text-muted-foreground">{facts.join(' · ')}</span> : null}
    </div>
  )
}

function AiCall({ row }: { row: TaxExemption }) {
  const call = aiCall(row)
  return <span className={cn('text-sm', AI_TONES[call.tone])}>{call.text}</span>
}

function Status({ row }: { row: TaxExemption }) {
  const status = STATUS[row.status]
  return (
    <div className="space-y-1">
      <StatusPill tone={status.tone}>{status.label}</StatusPill>
      <p className="text-xs tabular-nums text-muted-foreground">Uploaded {formatDay(row.created_at)}</p>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div aria-label="Loading" className="space-y-2" role="status">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton className="h-14 w-full" key={index} />
      ))}
    </div>
  )
}

/** Customers' tax-exemption forms (USA-1504): the ones a person must decide first. */
export function TaxExemptionsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useUrlSearchText()
  const [{ status, page }, setFilters] = useQueryStates({
    status: parseAsStringLiteral(TAX_EXEMPTION_FILTERS).withDefault('needs_review'),
    page: parseAsInteger.withDefault(1),
  })
  const list = useTaxExemptions(status, search.trim(), page)
  const rows = list.data?.items ?? []
  const pages = Math.max(list.data?.pages ?? 1, 1)
  const open = (row: TaxExemption) => void navigate({ to: '/tax-exemptions/$formId', params: { formId: row.uuid } })

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <h1 className="text-2xl font-bold tracking-tight">Tax exemptions</h1>

      <ListFilters
        count={resultCount(list.data?.total, 'form')}
        active={status === 'needs_review' ? 0 : 1}
        onClear={() => void setFilters({ status: null, page: 1 })}
        search={
          <label className="relative block">
            <span className="sr-only">Search tax exemptions</span>
            <Search aria-hidden="true" className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              className="pl-9"
              onChange={(event) => {
                setSearch(event.target.value)
                void setFilters({ page: 1 })
              }}
              placeholder="Customer, email, holder or number"
              value={search}
            />
          </label>
        }
      >
        <ListFilter label="Show">
          <SearchSelect
            allLabel={FILTER_LABELS.all}
            ariaLabel="Status filter"
            className="h-9 w-full"
            onChange={(next) => void setFilters({ status: (next as typeof status | undefined) ?? 'all', page: 1 })}
            options={FILTER_OPTIONS}
            searchPlaceholder="Search statuses"
            title="Show"
            value={status === 'all' ? undefined : status}
          />
        </ListFilter>
      </ListFilters>

      {list.isLoading ? (
        <ListSkeleton />
      ) : !rows.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {status === 'needs_review' && !search.trim() ? 'Nothing needs a person' : 'No forms'}
        </p>
      ) : (
        <>
          <div className="hidden lg:block">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>On the form</TableHead>
                  <TableHead className="w-[230px]">AI</TableHead>
                  <TableHead className="w-[170px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow className="cursor-pointer align-top" key={row.uuid} onClick={() => open(row)}>
                    <TableCell>
                      <Customer row={row} />
                    </TableCell>
                    <TableCell>
                      <OnTheForm row={row} />
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <AiCall row={row} />
                    </TableCell>
                    <TableCell>
                      <Status row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="grid gap-2 lg:hidden">
            {rows.map((row) => (
              <li
                className="cursor-pointer space-y-2 rounded-lg border bg-card p-3 hover:bg-muted/30"
                key={row.uuid}
                onClick={() => open(row)}
              >
                <div className="flex items-start justify-between gap-3">
                  <Customer row={row} />
                  <StatusPill tone={STATUS[row.status].tone}>{STATUS[row.status].label}</StatusPill>
                </div>
                <OnTheForm row={row} />
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <AiCall row={row} />
                  <span className="text-xs tabular-nums text-muted-foreground">Uploaded {formatDay(row.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {pages > 1 ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {list.data?.page ?? page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button disabled={page <= 1} onClick={() => void setFilters({ page: page - 1 })} size="sm" variant="outline">
              Previous
            </Button>
            <Button disabled={page >= pages} onClick={() => void setFilters({ page: page + 1 })} size="sm" variant="outline">
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
